import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function safeParse(str) {
  try {
    return str ? JSON.parse(str) : null;
  } catch {
    localStorage.removeItem('user');
    return null;
  }
}

/**
 * Authentication state:
 *  - The session token lives ONLY in an httpOnly cookie set by the backend (see routes/auth.py).
 *    JavaScript cannot read it; this mitigates XSS-based token theft.
 *  - The non-sensitive `user` object is kept in localStorage purely for client-side route guards
 *    (instant render of role-aware UI on reload). It is never used to prove identity to the server.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => safeParse(localStorage.getItem('user')));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await authAPI.getMe();
      const userData = response.data;
      if (mountedRef.current) {
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      }
      return userData;
    } catch (err) {
      if (err.response?.status === 401 && mountedRef.current) {
        setUser(null);
        localStorage.removeItem('user');
      }
      return null;
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    const initAuth = async () => {
      setLoading(true);
      await checkAuth();
      if (mountedRef.current) setLoading(false);
    };
    initAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await authAPI.login({ email, password });
      const { user: userData } = response.data;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      throw new Error(message);
    }
  };

  const register = async (data) => {
    setError(null);
    try {
      const response = await authAPI.register(data);
      const { user: userData } = response.data;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed';
      setError(message);
      throw new Error(message);
    }
  };

  const processOAuthSession = async (sessionId) => {
    setError(null);
    try {
      const response = await authAPI.processSession(sessionId);
      const { user: userData } = response.data;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'OAuth login failed';
      setError(message);
      throw new Error(message);
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout API error:', err.message);
    } finally {
      setUser(null);
      localStorage.removeItem('user');
    }
  };

  const isAdmin = user?.role === 'admin';
  const isStaff = user?.role === 'staff';
  const isClient = user?.role === 'client';

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        register,
        logout,
        processOAuthSession,
        checkAuth,
        isAdmin,
        isStaff,
        isClient,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
